import { UseEntityData } from "crqlar-react-common/dist/components/useEntity";
import { Alert, Autocomplete, Button, Chip, Divider, IconButton, Stack, Switch } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAllAgeCategories } from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { Sector } from "crqlar-react-common/dist/models/sector/sector.model";
import { useAllSectors } from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {
  FidelioConfig,
  FidelioCredentials,
  FidelioDataProcessingRules } from
"crqlar-react-common/dist/models/integrations/fidelio/fidelio.config";
import {
  FullSyncConfig,
  PeriodicSyncConfig,
  PollerConfig } from
"crqlar-react-common/dist/models/integrations/pollerConfig.model";
import { AgeCategory } from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
  fidelioConfigData: UseEntityData<FidelioConfig>;
}

const FidelioConfigForm = ({ fidelioConfigData }: Props) => {

  const { t } = useTranslation();
  const sectors: Sector[] = useAllSectors();

  const fidelioConfig = fidelioConfigData.item;
  const nameMissing = isRequiredTextMissing(fidelioConfigData, fidelioConfig.name);
  const setFidelioConfig = fidelioConfigData.setItem;

  const ageCategories = useAllAgeCategories();

  //region Handlers

  const updateField = (field: keyof FidelioConfig, value: any) => {
    setFidelioConfig({ ...fidelioConfig, [field]: value });
  };

  const updateCredentialsField = (field: keyof FidelioCredentials, value: any) => {
    setFidelioConfig({
      ...fidelioConfig,
      credentials: {
        ...fidelioConfig.credentials,
        [field]: value
      }
    });
  };

  const updatePollerConfig = (field: keyof PollerConfig, value: any) => {
    setFidelioConfig({
      ...fidelioConfig,
      pollerConfig: {
        ...fidelioConfig.pollerConfig,
        [field]: value
      }
    });
  };

  const updateFullSyncConfig = (field: keyof FullSyncConfig, value: any) => {
    updatePollerConfig('fullSync', {
      ...fidelioConfig.pollerConfig?.fullSync,
      [field]: value
    });
  };

  const updateQuickConfig = (field: keyof PeriodicSyncConfig, value: any) => {
    updatePollerConfig('quickSync', {
      ...fidelioConfig.pollerConfig?.quickSync,
      [field]: value
    });
  };

  const updateDataProcessingRules = (field: keyof FidelioDataProcessingRules, value: any) => {
    updateField('dataProcessingRules', {
      ...fidelioConfig.dataProcessingRules,
      [field]: value
    });
  };

  //endregion Handlers

  //region UI

  const credentialsSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Integration name')}
        tooltip={t('Internal label for identifying this Fidelio integration configuration.')}>

                    <TextField
          id={'name'}
          size={'small'}
          value={fidelioConfig.name || ''}
          error={nameMissing}
          fullWidth
          onChange={(e) => updateField('name', e.target.value)} />

                </Property>

                <Property
        title={t('URL')}
        tooltip={t('Fidelio API endpoint used by this integration.')}>

                    <TextField
          id={'url'}
          size={'small'}
          value={fidelioConfig.credentials?.url || ''}
          fullWidth
          onChange={(e) => updateCredentialsField('url', e.target.value)} />

                </Property>

                <Property
        title={t('Username')}
        tooltip={t('Username used to authenticate against Fidelio.')}>

                    <TextField
          id={'username'}
          size={'small'}
          value={fidelioConfig.credentials?.username || ''}
          fullWidth
          onChange={(e) => updateCredentialsField('username', e.target.value)} />

                </Property>

                <Property
        title={t('Password')}
        tooltip={t('Password used to authenticate against Fidelio.')}>

                    <TextField
          id={'password'}
          size={'small'}
          value={fidelioConfig.credentials?.password || ''}
          fullWidth
          type={'password'}
          onChange={(e) => updateCredentialsField('password', e.target.value)} />

                </Property>
            </Stack>
        </Stack>;


  const pollerConfig =
  <AdditionalProperties title={t('Data sync setup')} variant={'outlined'}>

            <Typography textAlign={'start'} sx={{ mt: 1 }}>
                {t('Full sync')}
            </Typography>

            <Property
      title={t('Profile days past')}
      tooltip={t('How far back full sync should look for profiles.')}>

                <TextField
        id={'profile-days-past'}
        size={'small'}
        value={fidelioConfig.pollerConfig?.fullSync?.profileDaysPast || ''}
        onChange={(e) => updateFullSyncConfig('profileDaysPast', parseInt(e.target.value))}
        fullWidth
        type={'number'} />

            </Property>

            <Property
      title={t('Reservation days past')}
      tooltip={t('How far back full sync should look for reservations.')}>

                <TextField
        id={'reservation-days-past'}
        size={'small'}
        value={fidelioConfig.pollerConfig?.fullSync?.reservationDaysPast || ''}
        onChange={(e) => updateFullSyncConfig('reservationDaysPast', parseInt(e.target.value))}
        fullWidth
        type={'number'} />

            </Property>

            <Property
      title={t('Stale timeout minutes')}
      tooltip={t('Minutes before a full sync job is treated as stale.')}>

                <TextField
        id={'stale-timeout-minutes'}
        size={'small'}
        value={fidelioConfig.pollerConfig?.fullSync?.staleTimeoutMinutes || ''}
        onChange={(e) => updateFullSyncConfig('staleTimeoutMinutes', parseInt(e.target.value))}
        fullWidth
        type={'number'} />

            </Property>

            <Typography textAlign={'start'} sx={{ mt: 2 }}>
                {t('Quick sync')}
            </Typography>

            <Property
      title={t('Enabled')}
      tooltip={t('Turns scheduled quick sync on or off.')}>

                <Switch
        id={'quick-sync-scheduled'}
        checked={fidelioConfig.pollerConfig?.quickSync?.enabled || false}
        onChange={(e) => updateQuickConfig('enabled', e.target.checked)} />

            </Property>

            <Property
      title={t('Stale timeout minutes')}
      tooltip={t('Minutes before a quick sync job is treated as stale.')}>

                <TextField
        id={'quick-stale-timeout-minutes'}
        size={'small'}
        value={fidelioConfig.pollerConfig?.quickSync?.staleTimeoutMinutes || ''}
        onChange={(e) => updateQuickConfig('staleTimeoutMinutes', parseInt(e.target.value))}
        fullWidth
        type={'number'} />

            </Property>

            <Property
      title={t('Sync period minutes')}
      tooltip={t('Minutes between scheduled quick sync runs.')}>

                <TextField
        id={'quick-sync-period-minutes'}
        size={'small'}
        value={fidelioConfig.pollerConfig?.quickSync?.syncPeriodMinutes || ''}
        onChange={(e) => updateQuickConfig('syncPeriodMinutes', parseInt(e.target.value))}
        fullWidth
        type={'number'} />

            </Property>

        </AdditionalProperties>;


  const dataProcessingRules =
  <AdditionalProperties title={t('Data processing rules')} variant={'outlined'}>

            <Property
      title={t('Excluded room IDs')}
      tooltip={t('Fidelio room identifiers that should be ignored during import.')}>

                <Autocomplete
        size={'small'}
        clearIcon={false}
        options={[]}
        freeSolo
        multiple
        renderTags={(value, props) =>
        value.map((option, index) =>
        <Chip size={'small'} label={option} {...props({ index })} />
        )
        }
        renderInput={(params) => <TextField label={t("Add Values")} {...params} />}
        sx={{ width: '100%', minWidth: 200 }}
        value={fidelioConfig.dataProcessingRules?.excludedRoomIds || []}
        onChange={(e, value) => {
          updateDataProcessingRules('excludedRoomIds', value);
        }} />

            </Property>

            <Property
      title={t('Reservation status to keep')}
      tooltip={t('Only reservations with these Fidelio status codes are imported or kept.')}>

                <Stack gap={1} width={'100%'}>
                    <Alert
          variant={'outlined'}
          color={'info'}
          sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>
                        {t("0 = tentative")}
          <br />
                        {t("1 = definitive")}<br />
                        {t("3 = not affecting availability")}
                    </Alert>

                    <Autocomplete
          size={'small'}
          clearIcon={false}
          options={[]}
          freeSolo
          multiple
          renderTags={(value, props) =>
          value.map((option, index) =>
          <Chip size={'small'} label={option} {...props({ index })} />
          )
          }
          renderInput={(params) => <TextField label={t("Add Values")} {...params} />}
          sx={{ width: '100%', minWidth: 200 }}
          value={fidelioConfig.dataProcessingRules?.reservationStatusToKeep || []}
          onChange={(e, value) => {
            updateDataProcessingRules('reservationStatusToKeep', value);
          }} />

                </Stack>
            </Property>

            <Property
      title={t('Reservation states to keep')}
      tooltip={t('Only reservations with these Fidelio state codes are imported or kept.')}>

                <Stack gap={1} width={'100%'}>
                    <Alert
          variant={'outlined'}
          color={'info'}
          sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                        0 = Reservation - future reservation or reservation which is checked in but not yet checked
                        out<br />
                        {t("1 = Checked In (reservations checked in on the actual date (system date))")}<br />
                        {t("2 = Checked Out")}<br />
                        {t("3 = Canceled")}<br />
                        {t("4 = No Show")}<br />
                        {t("5 = Offer")}
                    </Alert>

                    <Autocomplete
          size={'small'}
          clearIcon={false}
          options={[]}
          freeSolo
          multiple
          renderTags={(value, props) =>
          value.map((option, index) =>
          <Chip size={'small'} label={option} {...props({ index })} />
          )
          }
          renderInput={(params) => <TextField label={t("Add Values")} {...params} />}
          sx={{ width: '100%', minWidth: 200 }}
          value={fidelioConfig.dataProcessingRules?.reservationStatesToKeep || []}
          onChange={(e, value) => {
            updateDataProcessingRules('reservationStatesToKeep', value);
          }} />

                </Stack>
            </Property>

            <Property
      title={t('Accommodation sector')}
      tooltip={t('Sector where imported accommodation reservations and resources should be mapped.')}>

                <Autocomplete
        id={'sectors'}
        options={sectors}
        getOptionLabel={(option: any) => option.name || ''}
        value={sectors.find((s) => s.id === fidelioConfig.dataProcessingRules?.accommodationSectorId) || null}
        renderInput={(params) =>
        <TextField
          {...params}
          fullWidth
          size={'small'} />

        }
        fullWidth
        onChange={(e, value) => {
          updateDataProcessingRules('accommodationSectorId', value?.id);
        }} />

            </Property>

            <Property
      title={t('Adults age category')}
      tooltip={t('Age category used for adult guest counts imported from Fidelio.')}>

                <Autocomplete
        id={'adults-age-categories'}
        options={ageCategories}
        getOptionLabel={(option: AgeCategory) => option.name || ''}
        value={ageCategories.find((s) => s.id === fidelioConfig.dataProcessingRules?.adultsAgeCategoryId) || null}
        renderInput={(params) =>
        <TextField
          {...params}
          fullWidth
          size={'small'} />

        }
        fullWidth
        onChange={(e, value) => {
          updateDataProcessingRules('adultsAgeCategoryId', value?.id || undefined);
        }} />

            </Property>

            <Property
      title={t('Children age category')}
      tooltip={t('Age category used for child guest counts imported from Fidelio.')}>

                <Autocomplete
        id={'children-age-categories'}
        options={ageCategories}
        getOptionLabel={(option: AgeCategory) => option.name || ''}
        value={ageCategories.find((s) => s.id === fidelioConfig.dataProcessingRules?.childrenAgeCategoryId) || null}
        renderInput={(params) =>
        <TextField
          {...params}
          fullWidth
          size={'small'} />

        }
        fullWidth
        onChange={(e, value) => {
          updateDataProcessingRules('childrenAgeCategoryId', value?.id || undefined);
        }} />

            </Property>

            <Property
      title={t('Children type to category ID')}
      tooltip={t('Maps Fidelio child type codes to local age categories.')}>

                <Stack gap={1} width={'100%'}>
                    {
        Object.keys(fidelioConfig.dataProcessingRules?.childrenTypeToCategoryId || {}).map((k) => {
          return (
            <Stack
              direction={'row'}
              key={k}
              width={'100%'}
              gap={1}>

                                    <TextField
                value={k}
                variant={'outlined'}
                size={'small'}
                sx={{ width: '50%' }}
                onChange={(e) => {
                  const newAssignments = { ...fidelioConfig.dataProcessingRules?.childrenTypeToCategoryId };
                  delete newAssignments[k];
                  newAssignments[e.target.value] = fidelioConfig.dataProcessingRules?.childrenTypeToCategoryId?.[k];
                  updateDataProcessingRules('childrenTypeToCategoryId', newAssignments);
                }} />


                                    <Autocomplete
                id={`children-type-age-category-${k}`}
                value={ageCategories.find((ac) => ac.id === fidelioConfig.dataProcessingRules?.childrenTypeToCategoryId?.[k]) || null}
                getOptionLabel={(option) => option.name || ''}
                options={ageCategories}
                fullWidth
                onChange={(e, value) => {
                  const newAssignments = { ...fidelioConfig.dataProcessingRules?.childrenTypeToCategoryId };
                  newAssignments[k] = value?.id || undefined;
                  updateDataProcessingRules('childrenTypeToCategoryId', newAssignments);
                }}
                renderInput={(params) =>
                <TextField
                  {...params}
                  size={'small'}
                  fullWidth />

                } />


                                    <IconButton
                onClick={() => {
                  const newAssignments = { ...fidelioConfig.dataProcessingRules?.childrenTypeToCategoryId };
                  delete newAssignments[k];
                  updateDataProcessingRules('childrenTypeToCategoryId', newAssignments);
                }}>

                                        <DeleteIcon sx={{
                  width: '24px',
                  height: '24px',
                  color: 'error.main'
                }} />
                                    </IconButton>

                                </Stack>);

        })
        }

                    <Button
          onClick={() => {
            updateDataProcessingRules('childrenTypeToCategoryId', {
              ...fidelioConfig.dataProcessingRules?.childrenTypeToCategoryId,
              ['New Type']: undefined
            });
          }}>

                        {t('Add new mapping')}
                    </Button>
                </Stack>
            </Property>

            <Property
      title={t('Auto create resource for a room if not found')}
      tooltip={t('Creates missing room resources automatically when imported Fidelio data references an unknown room.')}>

                <Switch
        id={'auto-create-resource-if-not-found'}
        checked={fidelioConfig.dataProcessingRules?.autoCreateResourceIfNotFound}
        onChange={(e) => updateDataProcessingRules('autoCreateResourceIfNotFound', e.target.checked)} />

            </Property>

            <Property
      title={t('Auto create service a room if not found')}
      tooltip={t('Creates missing services automatically when imported Fidelio rate-plan data references an unknown service.')}>

                <Switch
        id={'auto-create-service-if-not-found'}
        checked={fidelioConfig.dataProcessingRules?.autoCreateServiceForRatePlanIfNotFound}
        onChange={(e) => updateDataProcessingRules('autoCreateServiceForRatePlanIfNotFound', e.target.checked)} />

            </Property>

        </AdditionalProperties>;


  const configurationSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Property ID / Name (optional, multiproperty)')}
        tooltip={t('Optional property identifier assigned to all data imported through this connector.')}>

                    <Stack
          width={'100%'}
          gap={1}>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'error'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
            id={'name'}
            size={'small'}
            value={fidelioConfig.propertyId || ''}
            onChange={(e) => updateField('propertyId', e.target.value)}
            fullWidth
            required />

                    </Stack>

                </Property>

                {pollerConfig}
                {dataProcessingRules}
            </Stack>
        </Stack>;


  //endregion UI

  return (
    <Stack
      sx={{
        width: '100%',
        gap: 6
      }}>

            {credentialsSection}
            <Divider />
            {configurationSection}
        </Stack>);


};

export default FidelioConfigForm;
